import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { StaffInvitationInfo, TotpSetup } from '@ste/shared';
import { ApiRequestError, postJson, useAuth, type TokenResponse } from '../../api/auth.ts';
import { fetchStaffInvitation, startStaffTotp } from '../../api/team.ts';
import { Banner, Button, Spinner } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import logo from '../../theme/assets/logo.svg?raw';
import { inputClass, TotpCodeInput, TotpEnrollment } from '../auth/TotpSetup.tsx';

/** STE staff invitation: choose a password and set up TOTP in one step (ADR-0013) */
export function TeamInvitationPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState<StaffInvitationInfo>();
  const [setup, setSetup] = useState<TotpSetup>();
  const [loadError, setLoadError] = useState<string>();
  const [error, setError] = useState<string>();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchStaffInvitation(token)
      .then(async (invitation) => {
        setInfo(invitation);
        setName(invitation.name);
        setSetup(await startStaffTotp(token));
      })
      .catch((e: Error) => setLoadError(e.message));
  }, [token]);

  async function accept(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const tokens = await postJson<TokenResponse>('/team-invitations/accept', {
        token,
        name,
        password,
        totp: code,
      });
      useAuth.getState().adopt(tokens);
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : t('common.error'));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div
          className="mx-auto text-text"
          style={{ width: 200 }}
          dangerouslySetInnerHTML={{ __html: logo }}
        />
        {loadError && <Banner tone="danger" title={loadError} />}
        {!info && !loadError && <Spinner label={t('common.loading')} />}
        {info && (
          <form onSubmit={accept} className="space-y-5" noValidate>
            <div>
              <h1 className="text-xl font-semibold">{t('teamInvite.title')}</h1>
              <p className="mt-1 text-muted">
                {t('teamInvite.text', { role: t(`team.role.${info.globalRole}`) })}
              </p>
            </div>
            {error && <Banner tone="danger" title={error} />}

            <fieldset className="space-y-3">
              <legend className="mb-2 font-medium">{t('teamInvite.account')}</legend>
              <p className="text-sm text-muted">{info.email}</p>
              <label className="block">
                <span className="text-sm text-muted">{t('invite.name')}</span>
                <input
                  className={inputClass}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              </label>
              <label className="block">
                <span className="text-sm text-muted">{t('auth.password')}</span>
                <input
                  type="password"
                  className={inputClass}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <span className="mt-1 block text-sm text-muted">{t('teamInvite.passwordHint')}</span>
              </label>
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="mb-2 font-medium">{t('teamInvite.second')}</legend>
              {setup ? <TotpEnrollment setup={setup} /> : <Spinner label={t('common.loading')} />}
              <TotpCodeInput value={code} onChange={setCode} />
            </fieldset>

            <Button
              type="submit"
              className="w-full"
              disabled={busy || !setup || !name.trim() || password.length < 10 || code.length !== 6}
            >
              {t('teamInvite.submit')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
